import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useGetPickupLocationQuery } from '../redux/apis'; 
import { getDistance } from 'geolib';
import { selectUserLocation } from '../redux/slices/locationSlice';
import { formatDistanceLocal } from '../components/checkout/delivery/utils/pickupUtils'; // Assuming this path is correct

export const usePickupData = (itemsFromProps, cartBuyProducts, cartRentProducts) => {
  const userLocation = useSelector(selectUserLocation);
  const [failedLocations, setFailedLocations] = useState({});
  const [locationDistances, setLocationDistances] = useState({});

  const pickupItems = useMemo(() => {
    if (itemsFromProps && itemsFromProps.length > 0) return itemsFromProps;
    return [...cartBuyProducts, ...cartRentProducts].filter(item => item.deliveryMethod === 'pickup');
  }, [itemsFromProps, cartBuyProducts, cartRentProducts]);

  const itemsByPickupLocation = useMemo(() => {
    if (!pickupItems.length) return {};
    const pickupLocationsMap = new Map();
    pickupItems.forEach(item => {
      let pickupAddressId = null;
      if (item.pickupAddress) {
        pickupAddressId = typeof item.pickupAddress === 'object' ? item.pickupAddress.id : item.pickupAddress;
      } else if (item.product?.pickupAddress) {
        pickupAddressId = typeof item.product.pickupAddress === 'object' ? item.product.pickupAddress.id : item.product.pickupAddress;
      }

      let merchantName = item.merchant?.name || item.merchant?.username || 'Unknown Seller';
      let merchantId = item.merchant?.id || 'unknown';

      if (pickupAddressId) {
        const locationKey = `address-${pickupAddressId}`;
        if (!pickupLocationsMap.has(locationKey)) {
          pickupLocationsMap.set(locationKey, {
            id: locationKey, addressId: pickupAddressId, city: null, postalCode: null, country: null,
            businessHours: null, pickupInstructions: null,
            latitude: item.pickupAddress?.latitude || item.product?.pickupAddress?.latitude || item.merchant?.address?.latitude,
            longitude: item.pickupAddress?.longitude || item.product?.pickupAddress?.longitude || item.merchant?.address?.longitude,
            merchants: new Set([merchantId]), merchantNames: new Set([merchantName]), items: []
          });
        }
        const group = pickupLocationsMap.get(locationKey);
        group.items.push(item);
        group.merchants.add(merchantId);
        group.merchantNames.add(merchantName);
      } else {
        let city = '', postalCode = '', latitude = null, longitude = null;
        if (item.pickupAddress && typeof item.pickupAddress === 'object') {
          city = item.pickupAddress.city || ''; postalCode = item.pickupAddress.postalCode || item.pickupAddress.zipCode || '';
          latitude = item.pickupAddress.latitude; longitude = item.pickupAddress.longitude;
        } else if (item.product?.pickupAddress && typeof item.product.pickupAddress === 'object') {
          city = item.product.pickupAddress.city || ''; postalCode = item.product.pickupAddress.postalCode || item.product.pickupAddress.zipCode || '';
          latitude = item.product.pickupAddress.latitude; longitude = item.product.pickupAddress.longitude;
        } else if (item.merchant?.address) {
          city = item.merchant.address.city || ''; postalCode = item.merchant.address.postalCode || item.merchant.address.zipCode || '';
          latitude = item.merchant.address.latitude; longitude = item.merchant.address.longitude;
        }
        const locationKey = `${city}-${postalCode}`;
        if (!pickupLocationsMap.has(locationKey)) {
          pickupLocationsMap.set(locationKey, {
            id: locationKey, city, postalCode, latitude, longitude,
            merchants: new Set([merchantId]), merchantNames: new Set([merchantName]), items: []
          });
        }
        const group = pickupLocationsMap.get(locationKey);
        group.items.push(item);
        group.merchants.add(merchantId);
        group.merchantNames.add(merchantName);
      }
    });
    const result = {};
    pickupLocationsMap.forEach((loc, key) => {
      result[key] = { ...loc, merchants: Array.from(loc.merchants), merchantNames: Array.from(loc.merchantNames) };
    });
    return result;
  }, [pickupItems]);

  const firstAddressIdToFetch = useMemo(() => {
    const locations = Object.values(itemsByPickupLocation);
    return locations.find(location => location.addressId)?.addressId || null;
  }, [itemsByPickupLocation]);

  const { data: pickupLocationApiData, isLoading: apiLoading, error: apiError } = useGetPickupLocationQuery(
    firstAddressIdToFetch, { skip: !firstAddressIdToFetch }
  );

  const enhancedLocations = useMemo(() => {
    const result = { ...itemsByPickupLocation };
    if (pickupLocationApiData && firstAddressIdToFetch) {
      Object.keys(result).forEach(key => {
        if (result[key].addressId === firstAddressIdToFetch) {
          result[key] = {
            ...result[key],
            city: pickupLocationApiData.city || result[key].city,
            postalCode: pickupLocationApiData.postalCode || result[key].postalCode,
            country: pickupLocationApiData.country,
            businessHours: pickupLocationApiData.businessHours,
            pickupInstructions: pickupLocationApiData.pickupInstructions,
            latitude: pickupLocationApiData.latitude || result[key].latitude,
            longitude: pickupLocationApiData.longitude || result[key].longitude,
          };
        }
      });
    }
    return result;
  }, [itemsByPickupLocation, pickupLocationApiData, firstAddressIdToFetch]);

  useEffect(() => {
    if (apiError && firstAddressIdToFetch) {
      setFailedLocations(prev => ({ ...prev, [firstAddressIdToFetch]: true }));
    }
  }, [apiError, firstAddressIdToFetch]);

  useEffect(() => {
    if (!userLocation?.coordinates || Object.keys(enhancedLocations).length === 0) {
      setLocationDistances({});
      return;
    }
    const distances = {};
    Object.entries(enhancedLocations).forEach(([locId, locData]) => {
      if (locData.latitude && locData.longitude) {
        try {
          const distMeters = getDistance(userLocation.coordinates, { latitude: locData.latitude, longitude: locData.longitude });
          distances[locId] = formatDistanceLocal(distMeters);
        } catch (err) {
          console.error('Error calculating distance for location:', locId, err);
        }
      }
    });
    setLocationDistances(distances);
  }, [enhancedLocations, userLocation]);

  const isLocationFailed = useCallback((location) => {
    return !!(location?.addressId && failedLocations[location.addressId]);
  }, [failedLocations]);

  return {
    pickupItems,
    enhancedLocations,
    locationDistances,
    isLoading: apiLoading, // Hook only exposes API loading, not internal grouping loading
    error: apiError, // Hook exposes API error
    isLocationFailed, // Function to check if a location is failed
    firstAddressIdForEffect: firstAddressIdToFetch // Expose for potential parent effects
  };
}; 